import { Link } from 'react-router-dom';
import { CONTROLADOR } from '@/config/controlador';

/**
 * Política de privacidade — reescrita em 26/09/2026.
 *
 * A versão de abril afirmava três coisas falsas: que as imagens da câmera "não são
 * armazenadas" (o envio de folhas as guarda), que havia coleta de "dados de uso"
 * (não há rastreamento nenhum) e que os relatos eram apagados com a conta (ficam,
 * sem autor). Cada frase abaixo que afirma uma CONDIÇÃO tem dono no código:
 *
 *   folhas por 12 meses ............ backend/src/services/retencaoDeFolhas.ts
 *   fotos de resposta aberta ....... backend/src/services/fotoDeResposta.ts: quem vê (`linkDaFoto` só
 *                                    depois de `assertPodeCorrigir` ou para o próprio aluno), 12 meses
 *                                    do fim do simulado e nunca antes do envio (`limparFotosVencidas`),
 *                                    sem os metadados da câmera (`anexarFoto` regrava a imagem), e a
 *                                    exclusão da conta leva as fotos (`apagarContaAnonimizando`; o
 *                                    arquivo sai na varredura diária, `varrerFotosSemLinha`)
 *   o que a exclusão apaga/mantém .. userService.excluirPropriaConta
 *   cache de 24 h .................. omrService.cleanupWarpCache
 *   registros do servidor, 14 dias . /etc/logrotate.d/nginx (rotate 14, diário)
 *   câmera não envia a foto ........ SimulationScannerPage (só `answers` e `meta`)
 *   pedidos: 7 e 90 dias ........... pedidoDeAcessoService.limparVencidos
 *   pedido aprovado sai com a conta  PedidoDeAcesso.contaCriada (onDelete: Cascade)
 *   IP do pedido só em memória ..... pedidoDeAcessoRoutes (express-rate-limit)
 *   aceite dos termos (data, versão) termosDeUso.aceitarTermos
 *   domínio aceito: só o domínio ... dominioAceito.aceitarDominio (InstitutionalDomain)
 *   banco em São Paulo ............. DATABASE_URL (região sa-east-1; conferido em 01/11/2026)
 *   cópias cifradas no B2 (EUA) .... backupDoBanco.ts e backupDosArquivos.ts (gpg com a chave PÚBLICA; a secreta
 *                                    fica fora do servidor — docs/BACKUP-DO-BANCO.md); bucket em us-east-005
 *
 * Mudou um desses, esta página muda junto. Ver "Comentário que afirma condição"
 * no CLAUDE.md — aqui a afirmação é pública, e a LGPD a lê.
 */
const PrivacyPage = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-16">
      <div className="container mx-auto px-4 max-w-3xl">
        <h1 className="font-titulo text-3xl font-bold text-tinta mb-2">Política de Privacidade</h1>
        <p className="text-sm text-gray-500 mb-8">Última atualização: 1º de novembro de 2026</p>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 space-y-6 text-gray-600 leading-relaxed">

          <section>
            <h2 className="text-xl font-semibold text-tinta mb-3">1. Quem somos</h2>
            <p>
              O Portal Simulados (site portalsimulados.com.br e aplicativos para iOS e Android) é operado por{' '}
              {CONTROLADOR.nome}. É uma plataforma de simulados e avaliações para escolas, usada
              por professores, equipes gestoras e estudantes a partir de 13 anos.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-tinta mb-3">2. Dados que tratamos</h2>
            <ul className="list-disc list-inside space-y-2">
              <li>
                <strong>Cadastro:</strong> nome, e-mail, nome de usuário, senha (guardada em forma de hash,
                que não permite recuperá-la), papel (estudante, professor ou equipe gestora), escola e turmas.
                O e-mail do estudante, quando informado, é o <strong>institucional</strong>
                (@soualuno.al.gov.br), informado pela escola a partir da relação da Secretaria de Estado da
                Educação. Dessa relação, só o RA e o e-mail chegam ao portal; os demais dados ficam no
                computador de quem faz a importação.
              </li>
              <li>
                <strong>Contas Google vinculadas:</strong> se você entra com o Google, guardamos o
                identificador que o Google atribui à conta (um número), o endereço de e-mail dela, se ela é
                institucional ou pessoal, e a data do vínculo. Do Google, o portal pede{' '}
                <strong>apenas a sua identidade</strong> (nome e e-mail). Não recebemos a sua senha do
                Google, nem acesso ao seu e-mail, agenda, arquivos ou contatos. O nome só é guardado quando
                a conta de professor é criada pelo próprio login institucional. A foto de perfil não é
                guardada.
              </li>
              <li>
                <strong>Aceite dos termos de uso:</strong> nas contas criadas por pedido de acesso, a data e a versão
                dos <Link to="/termos" className="text-marca-600 hover:underline">termos de uso</Link> que você
                aceitou no primeiro acesso.
              </li>
              <li>
                <strong>Respostas e resultados:</strong> as respostas a cada simulado, a nota, a data e a hora do
                envio, a duração da prova e o tipo de dispositivo (celular ou computador, deduzido do navegador).
              </li>
              <li>
                <strong>Folhas de resposta digitalizadas:</strong> quando o professor envia as folhas pelo portal
                (PDF ou foto), guardamos a imagem de cada página junto do resultado, como registro da correção. A
                folha contém o que o estudante escreveu nela, inclusive o nome. Só quem corrige aquele simulado tem
                acesso. As folhas são apagadas <strong>12 meses após o envio</strong>. Cópias de trabalho feitas
                durante a leitura são apagadas em até 24 horas.
              </li>
              <li>
                <strong>Fotos anexadas às respostas abertas:</strong> quando o professor permite, o estudante pode
                anexar à resposta de uma questão aberta a foto da resolução feita no papel. Guardamos a imagem junto
                da resposta, com a data e o tamanho. A foto contém o que o estudante escreveu, e pode conter o nome.
                Só têm acesso o próprio estudante, quem corrige aquele simulado e a equipe gestora da escola quando
                o simulado é da escola. A foto não fica em endereço público: cada acesso usa um link que vale poucos
                minutos. Ao receber a foto, o portal remove dela os dados que a câmera grava junto, entre eles a
                localização. As fotos são apagadas <strong>12 meses após o fim do simulado</strong> (e nunca antes
                de 12 meses do envio).
              </li>
              <li>
                <strong>Uso da página da prova:</strong> “Se você sair desta página durante a prova, o professor é
                avisado. A página registra o uso da prova: tempo em cada questão, navegação e trocas de resposta.”
                Na prova online e na sala sem internet, a página da prova anota as vezes em que deixou de estar
                visível (troca de aplicativo ou de aba, tela apagada) e por quanto tempo, o tempo com cada questão na
                tela, a ordem em que o estudante passou pelas questões, as alternativas que marcou antes da final, se
                colou texto numa resposta aberta (só o fato e o número de caracteres, nunca o texto), o tipo de
                aparelho (celular, tablet ou computador) e as quedas de conexão. Nada fora da página é registrado:
                nem câmera, nem tela, nem outros aplicativos. Esse registro fica junto da resposta e só o professor
                que pode corrigir o estudante, a gestão da escola e a administração o veem.
              </li>
              {/* Os dois parágrafos da 2.0.14 (aprovados pelo pesquisador em 29/10/2026), conferidos no código do aplicativo:
                  `desktop/src/sala/estado.js` (quedas: as últimas 50 e o total), `envio.js` (o que vai ao portal), `servidor.js`
                  (o teste guarda hora e endereço) e `aluno/prova/pagina.js` (o tempo só conta com a página visível). */}
              <li>
                <strong>Na sala sem internet (aplicativo do professor):</strong> durante a prova, o computador
                do professor anota também a <strong>questão em que o estudante está</strong> (só enquanto a prova acontece,
                para a tela do professor — não fica guardada); cada <strong>queda da conexão</strong> do celular com o
                computador, com a hora em que caiu e em que voltou (as últimas 50, e quantas foram ao todo); os
                <strong> minutos a mais</strong> que o professor deu à sala ou só àquele estudante; e se o professor
                <strong> encerrou a prova</strong> do estudante e se depois desfez. Esses três últimos vão ao portal com a
                entrega e aparecem para quem corrige a prova e na exportação de respostas do professor (a planilha da turma,
                com os nomes). Os <strong>recados</strong> que o professor manda pela sala ficam só no computador do
                professor e não vão ao portal. O <strong>teste de rede</strong> que o professor faz com o próprio celular
                guarda só a hora do teste e o endereço do celular na rede local, no registro da sala, no computador do
                professor. Quando a sala é aberta pelo celular ou tablet do professor, o mesmo registro fica nesse aparelho.
              </li>
              {/* O parágrafo da 2.0.15 (aprovado pelo pesquisador em 30/10/2026), conferido no código do aplicativo:
                  `desktop/src/sala/servidor.js` ("O CELULAR DO PROFESSOR": `painelDoCelular` tira acertos e nota; o registro
                  anota pediu, confirmado, recusado, saiu, voltou, revogado, o endereço local e o tipo do aparelho). */}
              <li>
                <strong>O celular do professor como painel da sala:</strong> o professor pode acompanhar a sala pelo
                próprio celular, na mesma rede local, depois de confirmar esse celular no computador da sala. O celular
                recebe do computador a lista dos estudantes da sala com a situação, a questão em que cada um está, quantas
                respondeu e a conexão — <strong>não recebe as respostas, nem acertos ou nota</strong>. Nada disso sai da
                rede da sala nem fica guardado no celular além da página aberta. O registro da sala, no computador do
                professor, anota quando o celular do professor pediu para entrar, foi confirmado ou recusado, saiu e
                voltou, o endereço dele na rede local, o tipo do aparelho (como “iPhone” ou “Android”, lido do navegador)
                e as ações feitas por ele (“pelo celular do professor”), como já anota as feitas no computador.
              </li>
              <li>
                <strong>Como o tempo é medido:</strong> o tempo de cada questão só conta enquanto a página da prova está à
                frente: com a tela bloqueada, em outro aplicativo ou em outra aba, ele para. Quando várias questões estão na
                mesma tela (os itens a, b e c de uma questão), o tempo da tela vale para cada uma delas. Questão que o
                estudante não abriu fica sem tempo. Esses dados servem ao professor, para entender o ritmo da prova —
                <strong> não dão nem tiram ponto</strong>, e o portal não marca nenhum estudante como “suspeito”. No
                relatório, o professor vê só quantos alunos tiveram respostas abaixo do tempo de leitura da questão.
              </li>
              <li>
                <strong>Câmera do aplicativo:</strong> a folha fotografada é lida no próprio aparelho. Ao servidor
                chegam apenas as respostas lidas; a foto não é enviada.
              </li>
              {/* O push (1.4.1), aprovado pelo pesquisador em 30/10/2026 — o texto da proposta, conferido no código:
                  `backend/src/services/push.ts` (os 7 tipos, o silêncio que adia, nada ao aluno em prova, a fila sem texto e
                  30 dias, o token apagado ao sair da conta) e `clientWeb/src/features/notificacoes/`. */}
              <li>
                <strong>Avisos no celular (aplicativo):</strong> se você permitir, o aplicativo recebe avisos do portal no
                celular: para o estudante, prova nova, prova que fecha em 24 horas sem entrega e resultado liberado; para o
                professor e a gestão, respostas esperando correção, simulado encerrado, sala sem internet sem resultados
                enviados e PDF pronto. Para isso, o portal guarda um identificador que o sistema do celular cria para o
                aplicativo (o “token” de notificação), ligado à sua conta, com o tipo do aparelho e a versão do aplicativo.
                O aviso é entregue pelo serviço de notificações do sistema: no Android, o Firebase Cloud Messaging, do
                Google; no iPhone, o Apple Push Notification service, da Apple. Esses serviços recebem o token e o texto do
                aviso, e nada mais. O texto é curto e não traz nota, acerto nem nome de outro estudante; o nome de um
                estudante só aparece no aviso do professor que ligar essa opção naquele aparelho. Nenhum aviso chega ao
                estudante durante uma prova, nem no horário de silêncio que você escolher (o aviso espera o fim do
                silêncio). O portal registra cada envio (tipo, quando e o resultado) por 30 dias, sem o texto. Você
                desliga os avisos em Minha conta ou nos ajustes do celular. Ao sair da conta no aplicativo, o celular deixa
                de receber os avisos dela, e o token é apagado.
              </li>
              <li>
                <strong>Relatos que você enviar:</strong> quando você usa o botão “Relatar algo”, guardamos o texto
                que você escreveu e, junto dele, o que o sistema anexa para tornar o relato investigável: a tela e o
                endereço em que você estava (sem os filtros de busca), seu papel e sua escola, se era navegador ou
                aplicativo, a versão do portal e a identificação do seu navegador. <strong>Não</strong> capturamos
                imagem da tela, conteúdo da página, respostas de estudantes nem dados de terceiros.
              </li>
              <li>
                <strong>Registros do servidor:</strong> endereço IP, data e hora e endereço acessado, guardados por
                14 dias para segurança e para conter abusos.
              </li>
            </ul>
            <p className="mt-3">
              <strong>O que não fazemos:</strong> não há rastreamento de navegação, ferramentas de análise de uso,
              publicidade, cookies de terceiros nem coleta de localização. O aplicativo não contém bibliotecas de
              terceiros que coletem dados.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-tinta mb-3">3. Para que usamos</h2>
            <ul className="list-disc list-inside space-y-1">
              <li>Autenticar e autorizar o acesso, inclusive pelo login com o Google.</li>
              <li>Aplicar e corrigir simulados — online, por folha digitalizada ou pela câmera do aplicativo.</li>
              <li>Gerar relatórios pedagógicos para a escola (desempenho por habilidade, pela matriz escolhida: Saeb, BNCC, Enem ou conteúdo; faixas de desempenho da turma; evolução).</li>
              <li>Responder aos relatos e corrigir defeitos.</li>
            </ul>
            <p className="mt-3">
              Resultados podem ser usados, de forma agregada e sem identificação dos estudantes, em pesquisa
              acadêmica sobre avaliação educacional conduzida com anuência da escola.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-tinta mb-3">4. Com quem compartilhamos</h2>
            <p>
              Não vendemos dados. Eles são vistos por professores e pela equipe gestora da sua escola, para fins
              pedagógicos, e podem ser fornecidos quando a lei ou uma ordem judicial exigir.
            </p>
            <p className="mt-2">Serviços que tratam dados em nosso nome:</p>
            <ul className="list-disc list-inside space-y-1 mt-2">
              <li>
                <strong>Supabase</strong> — banco de dados, em servidores localizados em <strong>São Paulo
                (Brasil)</strong>.
              </li>
              <li>
                <strong>Backblaze (B2)</strong> — guarda as cópias de segurança: a cópia diária do banco de dados e as
                cópias dos arquivos (figuras das questões, folhas digitalizadas, fotos de respostas abertas e documentos
                importados), em servidores nos <strong>Estados Unidos</strong> (transferência internacional, LGPD art. 33).
                As cópias saem do portal já cifradas, e a chave que as abre não fica no servidor do portal nem na
                Backblaze: fica com o responsável pelo portal. A Backblaze guarda arquivos que não consegue ler.
              </li>
              <li><strong>Cloudflare</strong> — rede e proteção do site.</li>
              <li>
                <strong>Google</strong> — envio de e-mails do portal (Gmail) e login com conta Google
                (“Entrar com Google”). No login, é o Google que informa ao portal quem você é. O portal não
                envia ao Google dados seus nem das suas respostas.
              </li>
              <li>
                <strong>Google (Firebase Cloud Messaging) e Apple (Apple Push Notification service)</strong> — entregam
                os avisos no celular, para quem os permitir no aplicativo: recebem o token do aparelho e o texto curto do
                aviso.
              </li>
              <li>
                <strong>Anthropic</strong> — processa apenas o texto de questões, ao organizá-las no acervo. Nenhum
                dado de estudante é enviado.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-tinta mb-3">5. Aplicação sem internet</h2>
            <p>
              Na aplicação sem internet, o nome, a turma e as respostas dos estudantes ficam no aparelho do professor que
              abre a sala — o computador (aplicativo para Windows e Mac) ou o celular ou tablet (aplicativo para iPhone,
              iPad e Android) — até serem enviados ao portal.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-tinta mb-3">6. Pedidos de acesso</h2>
            <p>
              Quem não é da rede estadual de Alagoas pode pedir acesso pelo formulário{' '}
              <Link to="/solicitar-acesso" className="text-marca-600 hover:underline">Solicitar acesso</Link>.
            </p>
            <ul className="list-disc list-inside space-y-2 mt-2">
              <li>
                <strong>O que guardamos:</strong> só o que o formulário pede — nome, e-mail, escola ou instituição,
                rede, cidade e UF, o motivo e o texto livre, se você o preencher. Nenhum documento, nenhum anexo.
                O endereço de internet (IP) serve apenas para limitar tentativas repetidas, na memória do servidor,
                e não é gravado com o pedido; como em qualquer visita, aparece nos registros do servidor por 14 dias.
              </li>
              <li>
                <strong>Para que:</strong> confirmar que o e-mail é seu, decidir o pedido e responder a ele.
              </li>
              <li>
                <strong>Quem vê:</strong> só o administrador do portal.
              </li>
              <li>
                <strong>Por quanto tempo:</strong> o pedido cujo e-mail não foi confirmado é apagado em 7 dias; o
                pedido recusado, 90 dias depois da decisão; o pedido aprovado fica enquanto a conta existir, como
                registro de por que ela foi criada, e é apagado junto com ela.
              </li>
              <li>
                <strong>Domínio aceito:</strong> ao aprovar um pedido, o administrador pode aceitar o domínio do seu
                e-mail (a parte depois do @) como domínio só de professores; a partir daí, quem tem e-mail nesse
                domínio se cadastra sem pedido. O que passa a constar da lista do cadastro é só o domínio — nenhum
                dado seu a mais.
              </li>
              <li>
                <strong>Termos de uso:</strong> a conta criada por pedido aceita os{' '}
                <Link to="/termos" className="text-marca-600 hover:underline">termos de uso</Link> no primeiro acesso;
                guardamos a data e a versão do aceite.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-tinta mb-3">7. Segurança</h2>
            <p>
              Toda comunicação usa HTTPS; senhas são guardadas em forma de hash; o acesso é feito por token. As folhas
              digitalizadas e as fotos de resposta não ficam em endereço público: só saem por uma rota que confere
              quem pede. Nenhum
              sistema é invulnerável — mantenha sua senha em sigilo.
              {' '}No login com o Google, o servidor do portal confere a assinatura do Google, se o e-mail
              foi verificado e, na conta institucional, o domínio da rede. Uma conta Google pessoal só entra
              depois que você a vincula, já dentro do portal, em Minha conta, e pode ser desvinculada ali a
              qualquer momento.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-tinta mb-3">8. Exclusão de conta</h2>
            <p>
              No aplicativo ou no site: <strong>Minha conta → Excluir minha conta</strong>. Seu nome, e-mail,
              usuário e senha são apagados, e também as contas Google vinculadas, as imagens das suas folhas de
              resposta e as fotos que você anexou a respostas abertas (o arquivo sai do servidor em até 24 horas).
              As respostas às provas
              que você entregou permanecem, <strong>sem vínculo com você</strong>, nas estatísticas da escola. Os
              relatos que você enviou permanecem, sem vínculo com você. O passo a passo completo, inclusive para
              quem não consegue entrar, está em{' '}
              <Link to="/excluir-conta" className="text-marca-600 hover:underline">portalsimulados.com.br/excluir-conta</Link>.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-tinta mb-3">9. Seus direitos</h2>
            <p>
              Pela LGPD (art. 18), você pode pedir: confirmação de que tratamos seus dados e acesso a eles;
              correção; anonimização, bloqueio ou eliminação; portabilidade; informação sobre com quem os
              compartilhamos; e revogação do consentimento. Pedidos pelo e-mail abaixo, respondidos em até 15 dias.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-tinta mb-3">10. Estudantes menores de idade</h2>
            <p>
              O portal se destina a estudantes a partir de 13 anos. As contas de estudantes são criadas pela escola
              ou pelo professor, que respondem pelo cadastro como controladores dos dados, conforme a Lei Geral de
              Proteção de Dados (LGPD — Lei 13.709/2018).
              {' '}O e-mail institucional do estudante é o fornecido pela rede estadual de ensino. O estudante
              não precisa de conta Google pessoal para usar o portal.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-tinta mb-3">11. Alterações nesta política</h2>
            <p>
              Mudanças relevantes serão avisadas no próprio portal. A data no topo indica a versão em vigor.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-tinta mb-3">12. Contato</h2>
            <p>
              <strong>{CONTROLADOR.nome}</strong><br />
              E-mail:{' '}
              <a href={`mailto:${CONTROLADOR.contato}`} className="text-marca-600 hover:underline">
                {CONTROLADOR.contato}
              </a>
            </p>
          </section>

          <div className="pt-4 border-t">
            <Link to="/" className="text-marca-600 hover:underline text-sm">
              Voltar para a página inicial
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;
